import CustomButton from "@components/buttons/CustomButton";
import { CountriesDropdown } from "@components/dropdowns/CountriesDropdown";
import { MerchantDropdown } from "@components/dropdowns/MerchantDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { institutionApis } from "@services/apis/Institution";
import { GetCountriesListData } from "@services/models/Common/GetCountriesList";
import { GetBusinessListData } from "@services/models/Institution/GetBusinessModelList";
import { GetLegalStatusListData } from "@services/models/Institution/GetLegalStatusList";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";

const AddInstitution = () => {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [emailId, setEmailId] = useState<string>("");
  const [legalEntityName, setLegalEntityName] = useState<string>("");
  const [businessUrl, setBusinessUrl] = useState<string>("");
  const [storeName, setStoreName] = useState<string>("");
  const [refBy, setRefBy] = useState<string>("");
  const [poiStatus, setPoiStatus] = useState<string>("pending");
  const [poaStatus, setPoaStatus] = useState<string>("pending");
  const [contactCountryCode, setContactCountryCode] = useState<
    number | undefined
  >(undefined);
  const [contactNumber, setContactNumber] = useState<string>("");
  const [countries, setCountries] = useState<GetCountriesListData[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<
    number | undefined
  >(undefined);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<
    string | undefined
  >(undefined);
  const [legalStatuses, setLegalStatuses] = useState<GetLegalStatusListData[]>(
    []
  );
  const [businessModes, setBusinessModes] = useState<GetBusinessListData[]>([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState<
    number | undefined
  >(undefined);
  const [selectedLegalStatus, setSelectedLegalStatus] = useState<
    number | undefined
  >(undefined);

  const [countriesDropdownOpen, setCountriesDropDownOpen] =
    useState<boolean>(false);

  const poiStatusOptions = [
    { id: 1, name: "pending" },
    { id: 2, name: "approved" },
    { id: 3, name: "needs_review" },
  ];

  const poaStatusOptions = [
    { id: 1, name: "pending" },
    { id: 2, name: "approved" },
    { id: 3, name: "needs_review" },
  ];

  const getCountries = async () => {
    setLoading(true);
    try {
      const response = await institutionApis.getCountriesList();
      if (response.data.length > 0) {
        setCountries(response.data);

        const defaultCountry = response.data.find(
          (country) => country.id === 95
        );
        const countryToSelect = defaultCountry || response.data[0];

        if (selectedCountryCode) {
          const userCountry = response.data.find(
            (country) => country.countryCode === selectedCountryCode
          );
          if (userCountry) {
            setSelectedCountryId(userCountry.id);
          } else {
            setSelectedCountryId(countryToSelect.id);
          }
        } else {
          setSelectedCountryId(countryToSelect.id);
        }

        if (!contactCountryCode) {
          setContactCountryCode(countryToSelect.id);
          setSelectedCountryCode(countryToSelect.countryCode);
        }
      } else {
        setFetchError(true);
      }
    } catch (error) {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const getLegalStatuses = async () => {
    try {
      setLoading(true);
      const response = await institutionApis.getLegalStatusList();
      setLegalStatuses(response.data);
      setSelectedLegalStatus(response.data[0].id);
    } catch (error) {
      setLoading(false);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const getBusinessModes = async () => {
    try {
      setLoading(true);
      const response = await institutionApis.getBusinessModeList();
      setBusinessModes(response.data);
      setSelectedBusinessType(response.data[0].id);
    } catch (error) {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLegalStatuses();
    getBusinessModes();
    getCountries();
  }, []);

  const handleBusinessSelection = async (value: string | number) => {
    const selectedId = Number(value);
    setSelectedBusinessType(selectedId);
  };

  const handleLegalSelection = async (value: string | number) => {
    const selectedId = Number(value);
    setSelectedLegalStatus(selectedId);
  };

  const handleCountrySelection = async (value: string | number) => {
    const selectedId = Number(value);
    setSelectedCountryId(selectedId);
  };

  const handleContactCountrySelection = async (value: string | number) => {
    const selectedId = Number(value);
    setContactCountryCode(selectedId);

    const selectedCountry = countries.find(
      (country) => country.id === selectedId
    );
    if (selectedCountry) {
      setSelectedCountryCode(selectedCountry.countryCode);
    }
    setCountriesDropDownOpen(false);
  };

  const handlePoiStatusSelection = (value: string | number) => {
    const selectedOption = poiStatusOptions.find(
      (option) => option.id === Number(value)
    );
    if (selectedOption) {
      setPoiStatus(selectedOption.name);
    }
  };

  const handlePoaStatusSelection = (value: string | number) => {
    const selectedOption = poaStatusOptions.find(
      (option) => option.id === Number(value)
    );
    if (selectedOption) {
      setPoaStatus(selectedOption.name);
    }
  };

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      const response = await institutionApis.createNewMerchant({
        legalEntityName,
        businessUrl,
        storeName,
        countryOfIncorporation: selectedCountryId!,
        legalStatusId: selectedLegalStatus!,
        businessModeId: selectedBusinessType!,
        email: emailId,
        firstName: firstName,
        lastName: lastName,
        contactCountryCode: selectedCountryCode!,
        contactNumber: contactNumber,
        refby: refBy ?? undefined,
        poiStatus: poiStatus ?? undefined,
        poaStatus: poaStatus ?? undefined,
      });
      handleSuccess({
        message: response.msg || "Institution added successfully",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating status failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setSubmitLoading(false);
    }
  };

  if (fetchError) {
    return (
      <div className="flex flex-col gap-3 mt-[20px] border border-border-subtle py-4 px-6 rounded-lg bg-bg-base w-full">
        <span className="text-text-status-red font-medium text-sm">
          Error fetching data. Please try again later.
        </span>
      </div>
    );
  } else
    return (
      <div className="flex flex-col gap-3 mt-[20px] border border-border-subtle py-4 px-6 rounded-lg bg-bg-base w-full">
        <div className="flex flex-row gap-8">
          <div className="flex flex-col gap-4 w-1/2">
            <GlobalTextInputField
              title="First Name*"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter First Name"
            />
            <GlobalTextInputField
              title="Last Name*"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter Last Name"
            />
            <MerchantDropdown
              listOfValues={poiStatusOptions}
              value={
                poiStatusOptions.find((option) => option.name === poiStatus)
                  ?.id ?? 1
              }
              onChange={handlePoiStatusSelection}
              label="POI Status"
              color="bg-page-body"
              expandedBorderColor="border-button-primary"
              baseColor="bg-page-body"
              borderColor="border-subtle"
            />
            <MerchantDropdown
              listOfValues={poaStatusOptions}
              value={
                poaStatusOptions.find((option) => option.name === poaStatus)
                  ?.id ?? 1
              }
              onChange={handlePoaStatusSelection}
              label="POA Status"
              color="bg-page-body"
              expandedBorderColor="border-button-primary"
              baseColor="bg-page-body"
              borderColor="border-subtle"
            />
            <GlobalTextInputField
              title="Email Address*"
              value={emailId}
              onChange={(e) => setEmailId(e.target.value)}
              placeholder="Enter Email Address"
              type="email"
            />
            <div className="relative flex flex-col space-y-[4px]">
              <GlobalTextInputField
                type="number"
                label="Contact number*"
                placeholder="Enter number"
                allowDecimal={false}
                prefixIconPadding="pl-[86px]"
                value={contactNumber}
                prefixIcon={
                  <div
                    className="flex flex-row space-x-[4px] items-center cursor-pointer"
                    onClick={() => {
                      setCountriesDropDownOpen(!countriesDropdownOpen);
                    }}
                  >
                    <span className="text-[12px] font-normal text-text-base">
                      + ({selectedCountryCode})
                    </span>
                    <span
                      className="material-symbols-outlined"
                      style={{
                        transform: countriesDropdownOpen
                          ? "rotate(180deg)"
                          : "",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      keyboard_arrow_down
                    </span>
                    <div className="h-[40px] w-[1px] bg-border-subtle"></div>
                  </div>
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value) && value.length <= 15) {
                    setContactNumber(value);
                  }
                }}
              />
              {countriesDropdownOpen && (
                <div className="absolute top-full mt-[4px] left-0 right-0 z-50 bg-white shadow-lg border border-border-subtle rounded-md">
                  <CountriesDropdown
                    listOfValues={countries.map((country) => ({
                      id: country.id,
                      country: country.country,
                      countryImg: country.countryImg!,
                    }))}
                    value={contactCountryCode ?? undefined}
                    onChange={handleContactCountrySelection}
                    label=""
                    color="bg-page-body"
                    expandedBorderColor="border-button-primary"
                    baseColor="bg-page-body"
                    borderColor="border-subtle"
                    expanded={countriesDropdownOpen}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4 w-1/2">
            <GlobalTextInputField
              title="Legal Entity Name*"
              value={legalEntityName}
              onChange={(e) => setLegalEntityName(e.target.value)}
              placeholder="Enter Legal Entity Name"
            />
            <GlobalTextInputField
              title="Business URL*"
              value={businessUrl}
              onChange={(e) => setBusinessUrl(e.target.value)}
              placeholder="Enter Business URL"
            />
            <GlobalTextInputField
              title="Store Name*"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              placeholder="Enter Store Name"
            />
            <GlobalTextInputField
              title="Referred By"
              value={refBy}
              onChange={(e) => setRefBy(e.target.value)}
              placeholder="Enter Referrer Information"
            />
            {!loading ? (
              <CountriesDropdown
                listOfValues={countries.map((country) => ({
                  id: country.id,
                  country: country.country,
                  countryImg: country.countryImg!,
                }))}
                value={selectedCountryId ?? undefined}
                onChange={handleCountrySelection}
                label="Country of Incorporation*"
                color="bg-page-body"
                expandedBorderColor="border-button-primary"
                baseColor="bg-page-body"
                borderColor="border-subtle"
              />
            ) : (
              <div className="flex flex-col">
                <span className="text-text-base font-normal text-sm">
                  Country of Incorporation*
                </span>
                <Skeleton height={44} className="m-0 p-0" />
              </div>
            )}
            {loading ? (
              <div className="flex flex-col">
                <span className="text-text-base font-normal text-sm">
                  Legal Status of Business*
                </span>
                <Skeleton height={44} className="m-0 p-0" />
              </div>
            ) : (
              <MerchantDropdown
                listOfValues={legalStatuses.map((legalStatus) => ({
                  id: legalStatus.id,
                  name: legalStatus.legalStatus,
                }))}
                value={selectedLegalStatus ?? undefined}
                onChange={handleLegalSelection}
                label="Legal Status of Business*"
                color="bg-page-body"
                expandedBorderColor="border-button-primary"
                baseColor="bg-page-body"
                borderColor="border-subtle"
              />
            )}
            {!loading ? (
              <MerchantDropdown
                listOfValues={businessModes.map((businessMode) => ({
                  id: businessMode.id,
                  name: businessMode.businessMode,
                }))}
                value={selectedBusinessType ?? undefined}
                onChange={handleBusinessSelection}
                label="Mode of Business*"
                color="bg-page-body"
                expandedBorderColor="border-button-primary"
                baseColor="bg-page-body"
                borderColor="border-subtle"
              />
            ) : (
              <div className="flex flex-col">
                <span className="text-text-base font-normal text-[14px]">
                  Mode of Business*
                </span>
                <Skeleton height={44} className="m-0 p-0" />
              </div>
            )}
          </div>
        </div>
        <CustomButton
          text="CONTINUE"
          isActive={
            !!(
              legalEntityName &&
              businessUrl &&
              storeName &&
              selectedCountryId &&
              selectedLegalStatus &&
              selectedBusinessType
            )
          }
          onClick={handleSubmit}
          isButtonLoading={submitLoading}
        />
      </div>
    );
};

export default AddInstitution;
